// PartnersSection.jsx — updated with certifications from suppliers.js

import { useEffect, useRef, useState } from 'react'
import { suppliers } from '@data/suppliers'

function useFadeIn() {
  const ref = useRef(null)
  const [vis, setVis] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true) },
      { threshold: 0.1 }
    )
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [])
  return [ref, vis]
}

function LogoBadge({ supplier }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center',
      gap: '8px', padding: '6px 14px',
      background: '#f5f5f3', border: '1px solid #e0e0da',
    }}>
      <div style={{
        width: '6px', height: '26px',
        background: supplier.brandColor || '#007935',
        flexShrink: 0,
      }} />
      <span style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '13px', fontWeight: 700,
        letterSpacing: '0.10em', textTransform: 'uppercase',
        color: '#1a1a18', lineHeight: 1,
      }}>
        {supplier.shortName || supplier.name}
      </span>
    </div>
  )
}

export default function PartnersSection() {
  const [ref, vis] = useFadeIn()

  return (
    <section style={{ background: '#ffffff', padding: '72px 0' }}>
      <div style={{
        maxWidth: 'var(--container-max)',
        margin:   '0 auto',
        padding:  '0 var(--container-padding)',
      }}>

        <h2 style={{
          fontFamily:    "'DM Sans', sans-serif",
          fontSize:      'clamp(1.5rem, 2.5vw, 2rem)',
          fontWeight:    400,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color:         '#007935',
          margin:        '0 0 8px',
        }}>
          Strong partnerships
        </h2>
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize:   '14px', color: '#88877f',
          margin:     '0 0 48px', maxWidth: '520px',
        }}>
          Salient Agrinutrition is proud to work with these internationally
          recognised manufacturers and associations.
        </p>

        {/* Grid */}
        <div
          ref={ref}
          className="partners-grid"
          style={{
            display:             'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap:                 '1px',
            background:          '#e8e8e8',
          }}
        >
          {suppliers.map((s, i) => (
            <a
              key={s.id}
              href={s.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display:        'flex',
                flexDirection:  'column',
                alignItems:     'flex-start',
                padding:        '32px 28px',
                background:     '#ffffff',
                textDecoration: 'none',
                opacity:        vis ? 1 : 0,
                transform:      vis ? 'translateY(0)' : 'translateY(12px)',
                transition:     `opacity 0.5s ease ${i * 0.1}s,
                                 transform 0.5s ease ${i * 0.1}s,
                                 background 0.2s ease`,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f9f9f7' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffffff' }}
            >

              {/* Logo / badge */}
              <div style={{ height: '48px', display: 'flex', alignItems: 'center', marginBottom: '35px' }}>
                {s.logo ? (
                  <img
                    src={s.logo}
                    alt={s.name}
                    style={{
                      height: '80px', width: 'auto', maxWidth: '160px',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <LogoBadge supplier={s} />
                )}
              </div>

              {/* Name */}
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '18px', fontWeight: 700,
                color: '#1a1a18', margin: '0 0 4px', lineHeight: 1.2,
              }}>
                {s.name}
              </p>

              {/* Country */}
              <p style={{
                fontFamily:    "'DM Sans', sans-serif",
                fontSize:      '10px', fontWeight: 700,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                color:         '#007935', margin: '0 0 10px',
              }}>
                {s.flag} {s.country}
              </p>

              {/* Description */}
              <p style={{
                fontFamily:      "'DM Sans', sans-serif",
                fontSize:        '13px', color: '#6b6a63',
                lineHeight:      1.6, margin: '0 0 14px', flex: 1,
                display:         '-webkit-box',
                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {s.description}
              </p>

              {/* ── Certifications — NEW from updated suppliers.js ── */}
              {s.certifications && s.certifications.length > 0 && (
                <div style={{
                  display:      'flex',
                  flexWrap:     'wrap',
                  gap:          '5px',
                  marginBottom: '14px',
                }}>
                  {s.certifications.map(cert => (
                    <span
                      key={cert}
                      style={{
                        fontFamily:    "'DM Sans', sans-serif",
                        fontSize:      '10px',
                        fontWeight:    700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color:         '#065e32',
                        background:    'rgba(0,121,53,0.08)',
                        padding:       '3px 8px',
                        border:        '1px solid rgba(0,121,53,0.20)',
                      }}
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              )}

              {/* Visit link */}
              <span style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize:   '12px', fontWeight: 600,
                color:      '#007935',
                display:    'flex', alignItems: 'center', gap: '4px',
              }}>
                Visit website
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6h8M6 2l4 4-4 4"
                    stroke="currentColor" strokeWidth="1.5"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>

            </a>
          ))}
        </div>

      </div>

      <style>{`
        @media (max-width: 767px) {
          .partners-grid { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .partners-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </section>
  )
}